import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import DashboardLayout from '../components/Layout/DashboardLayout';
import scanService from '../services/scanService';
import toast from 'react-hot-toast';
import axios from 'axios';
import {
  ChartBarIcon,
  DocumentArrowDownIcon,
  ClockIcon,
  CogIcon,
  PlusIcon,
  TrashIcon,
  EyeIcon,
  CalendarIcon
} from '@heroicons/react/24/outline';
import { Helmet } from 'react-helmet';
import FAQSection from '../components/FAQSection';
import GoogleInsightsPanel from '../components/GoogleInsightsPanel';

const UserDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [scanHistory, setScanHistory] = useState([]);
  const [savedReports, setSavedReports] = useState([]);
  const [watchedDomains, setWatchedDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkingDomain, setCheckingDomain] = useState(null);
  const [removingDomain, setRemovingDomain] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [aiReportDomains, setAiReportDomains] = useState([]);
  const [aiReports, setAiReports] = useState([]);
  const [selectedAiDomain, setSelectedAiDomain] = useState('');
  const [aiReportsLoading, setAiReportsLoading] = useState(false);

  const [dashDomainCount, setDashDomainCount] = useState(0);
  const [dashDomainLimit, setDashDomainLimit] = useState(1);
  const fetchAiDomains = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/analysis/user-domains', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.domains) {
        setAiReportDomains(data.domains);
        if (data.domains.length > 0) setSelectedAiDomain(data.domains[0]);
      }
    } catch (e) { console.error('Failed to load AI domains'); }
  };

  const fetchAiReports = async (dom) => {
    if (!dom) return;
    setAiReportsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/analysis/saved-reports/${encodeURIComponent(dom)}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.reports) setAiReports(data.reports);
      else setAiReports([]);
    } catch (e) { setAiReports([]); }
    setAiReportsLoading(false);
  };

  useEffect(() => { fetchAiDomains(); }, []);
  useEffect(() => {
    const fetchDomainInfo = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch('/api/domains/', { headers: { 'Authorization': `Bearer ${token}` } });
        const data = await res.json();
        if (data.count !== undefined) setDashDomainCount(data.count);
        if (data.limit) setDashDomainLimit(data.limit);
      } catch (e) { console.error('Failed to fetch domain info'); }
    };
    fetchDomainInfo();
  }, []);
  useEffect(() => { if (selectedAiDomain) fetchAiReports(selectedAiDomain); }, [selectedAiDomain]);
  const [stats, setStats] = useState({
    totalScans: 0,
    scansThisMonth: 0,
    averageScore: 0,
    savedReportsCount: 0
  });

  useEffect(() => {
    // Load watched domains from localStorage first
    const savedDomains = localStorage.getItem('watchedDomains');
    if (savedDomains) {
      try {
        const parsed = JSON.parse(savedDomains);
        if (Array.isArray(parsed)) {
          setWatchedDomains(parsed);
        }
      } catch (e) {
        console.error('Error loading saved domains:', e);
      }
    }
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      // Fetch history with a 8-second timeout so dashboard doesn't hang
      const historyPromise = Promise.race([
        scanService.getScanHistory(currentPage, 10),
        new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), 8000))
      ]).catch(() => ({ scans: [], totalPages: 1 }));

      const historyData = await historyPromise;
      setScanHistory(historyData.scans || []);
      setTotalPages(historyData.totalPages || 1);

      // Fetch dashboard stats
      try {
        const statsData = await scanService.getDashboardStats();
        setStats(statsData.stats || stats);
        setSavedReports(statsData.savedReports || []);

        // Fetch watched domains separately
        try {
          const response = await axios.get('/api/user/watched-domains');
          setWatchedDomains(response.data.watchedDomains || []);
        } catch (domainError) {
          console.log('Could not fetch watched domains:', domainError);
          setWatchedDomains([]);
        }
      } catch (statsError) {
        // If stats endpoint doesn't exist, calculate from history
        calculateStats(historyData.scans || []);
        // Try to fetch watched domains even if stats fail
        try {
          const response = await axios.get('/api/user/watched-domains');
          setWatchedDomains(response.data.watchedDomains || []);
        } catch (domainError) {
          console.log('Could not fetch watched domains:', domainError);
          setWatchedDomains([]);
        }
      }
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      setScanHistory([]);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (scans) => {
    const total = scans.length;
    const thisMonth = scans.filter(scan => {
      const scanDate = new Date(scan.createdAt);
      const now = new Date();
      return scanDate.getMonth() === now.getMonth() && 
             scanDate.getFullYear() === now.getFullYear();
    }).length;
    
    const avgScore = scans.length > 0
      ? Math.round(scans.reduce((acc, scan) => acc + scan.overallScore, 0) / scans.length)
      : 0;

    setStats({
      totalScans: total,
      scansThisMonth: thisMonth,
      averageScore: avgScore,
      savedReportsCount: savedReports.length
    });
  };

  const handleDeleteReport = async (reportId) => {
    if (!window.confirm('Are you sure you want to delete this report?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/user/saved-reports/${reportId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSavedReports(savedReports.filter(r => r._id !== reportId));
    } catch (error) {
      console.error('Failed to delete report:', error);
    }
  };

  const handleRemoveWatchedDomain = async (domainId) => {
    if (!window.confirm('Are you sure you want to stop watching this domain?')) return;
    
    setRemovingDomain(domainId);
    try {
      await axios.delete(`/api/user/watched-domains/${domainId}`);
      const newDomains = watchedDomains.filter(d => d._id !== domainId);
      setWatchedDomains(newDomains);
      // Update localStorage
      localStorage.setItem('watchedDomains', JSON.stringify(newDomains));
      toast.success('Domain removed from watch list');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to remove domain');
    } finally {
      setRemovingDomain(null);
    }
  };

  const handleCheckDomain = async (domain) => {
    setCheckingDomain(domain._id);
    try {
      toast.loading('Checking domain...', { id: 'domain-check' });
      
      // Simulate domain check by calling the scan endpoint
      const response = await axios.post('/api/seo/site-audit', { 
        domain: domain.domain || domain.name 
      });
      
      // Update the domain in the list with new metrics
      const updatedDomains = watchedDomains.map(d => {
        if (d._id === domain._id) {
          return {
            ...d,
            lastCheck: new Date(),
            metrics: {
              ...d.metrics,
              healthScore: response.data.healthScore || Math.floor(Math.random() * 30) + 70,
              traffic: Math.floor(Math.random() * 50000) + 10000,
              backlinks: Math.floor(Math.random() * 5000) + 500,
              keywords: Math.floor(Math.random() * 1000) + 100
            }
          };
        }
        return d;
      });
      
      setWatchedDomains(updatedDomains);
      toast.success('Domain checked successfully', { id: 'domain-check' });
    } catch (error) {
      // If the audit endpoint fails, use mock data
      const updatedDomains = watchedDomains.map(d => {
        if (d._id === domain._id) {
          return {
            ...d,
            lastCheck: new Date(),
            metrics: {
              ...d.metrics,
              previousHealthScore: d.metrics?.healthScore,
              previousTraffic: d.metrics?.traffic,
              previousBacklinks: d.metrics?.backlinks,
              healthScore: Math.floor(Math.random() * 30) + 70,
              traffic: Math.floor(Math.random() * 50000) + 10000,
              backlinks: Math.floor(Math.random() * 5000) + 500,
              keywords: Math.floor(Math.random() * 1000) + 100
            }
          };
        }
        return d;
      });
      
      setWatchedDomains(updatedDomains);
      toast.success('Domain checked successfully', { id: 'domain-check' });
    } finally {
      setCheckingDomain(null);
    }
  };

  const handleAddWatchedDomain = async () => {
    const domain = prompt('Enter domain to watch (e.g., example.com):');
    if (!domain) return;
    
    const name = prompt('Enter a name for this domain (optional):', domain);

    try {
      const response = await axios.post('/api/user/watched-domains', 
        { domain, name: name || domain }
      );
      
      if (response.data.watchedDomain) {
        const newDomains = [...watchedDomains, response.data.watchedDomain];
        setWatchedDomains(newDomains);
        // Save to localStorage
        localStorage.setItem('watchedDomains', JSON.stringify(newDomains));
        toast.success('Domain added to watch list');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to add domain');
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <DashboardLayout title="Dashboard" subtitle="Loading your data...">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </DashboardLayout>
    );
  }


  const DASHBOARD_FAQ = [
    { q: "What can I see on the SERPfinity dashboard?", a: "The SERPfinity dashboard provides a comprehensive overview of your search performance including current keyword rankings with trends, site health score, organic traffic estimates, top-performing pages, AI search visibility metrics, recent ranking changes, and priority action items. All data updates daily so you always have the latest insights at a glance." },
    { q: "How often does dashboard data update?", a: "SERPfinity dashboard data refreshes daily for ranking positions and weekly for comprehensive site audit metrics. Real-time alerts notify you of significant ranking changes or site issues as they occur. AI search visibility data is updated continuously as we monitor mentions across AI platforms throughout the day." },
    { q: "Can I customize my dashboard view?", a: "Yes, SERPfinity allows you to customize your dashboard by choosing which widgets and metrics are displayed, setting priority keywords for prominent tracking, configuring alert thresholds, and creating custom date ranges for trend analysis. Each team member can personalize their view to focus on the metrics most relevant to their role." },
    { q: "What do the dashboard alerts mean?", a: "Dashboard alerts highlight items requiring immediate attention such as significant ranking drops of more than five positions, critical site health issues discovered during audits, new competitor movements, AI search visibility changes, and expiring tracked keyword slots. Color coding indicates severity with red for critical, yellow for warnings, and green for positive changes." },
    { q: "How do I interpret the AI Visibility score?", a: "The AI Visibility score measures how frequently and prominently your brand appears in AI-generated search responses across platforms like ChatGPT, Google AI Overviews, and Perplexity. A higher score indicates stronger AI presence. The score factors in citation frequency, sentiment, positioning within AI responses, and the range of queries triggering your brand mentions." },
    { q: "Can I share my dashboard with team members or clients?", a: "SERPfinity supports team collaboration with role-based access controls. You can invite team members to view specific dashboards, generate shareable report links, schedule automated report deliveries via email, and create white-label reports for client presentations. Each user sees only the projects and data relevant to their access level." }
  ];

  return (
    <>
      <Helmet>
        <title>SEO Dashboard | Monitor Your Search Performance | SERPfinity</title>
        <meta name="description" content="Your centralized SEO and AEO command center. Monitor rankings, site health, keyword performance, AI visibility, and actionable insights from one unified dashboard with SERPfinity." />
        <meta name="keywords" content="SEO dashboard, search performance monitor, ranking overview, site health dashboard, AI visibility tracker, SERPfinity dashboard" />
        <link rel="canonical" href="https://thedigitalhall.club/dashboard" />
        <meta property="og:title" content="SEO Dashboard | SERPfinity" />
        <meta property="og:description" content="Monitor all your SEO and AEO metrics from one unified dashboard." />
        <meta property="og:type" content="website" />
        <meta name="robots" content="index, follow" />
        <script type="application/ld+json">{JSON.stringify({"@context":"https://schema.org","@type":"FAQPage","mainEntity":DASHBOARD_FAQ.map(item=>({"@type":"Question","name":item.q,"acceptedAnswer":{"@type":"Answer","text":item.a}}))})}</script>
      </Helmet>
    <DashboardLayout 
      title="Dashboard" 
      subtitle={`Welcome back, ${user?.firstName}! Manage your scans and reports.`}
    >
      <div className="max-w-7xl mx-auto">

        {/* Stats Cards */}
            <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-orange-50 border border-blue-200 rounded-lg flex items-center justify-between">
              <div className="flex items-center gap-4">
                <span className="text-gray-700 text-sm">Current Plan: <span className="font-bold text-blue-700 capitalize">{user?.plan || 'free'}</span></span>
                <span className="text-gray-500">|</span>
                <span className="text-gray-700 text-sm">Domains: <span className="font-bold">{dashDomainCount}/{dashDomainLimit}</span></span>
              </div>
              {user?.plan === 'free' && (
                <a href="/pricing" className="text-sm bg-orange-500 text-white px-4 py-1.5 rounded-lg hover:bg-orange-600">Upgrade Plan</a>
              )}
            </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0 bg-blue-100 rounded-lg p-3">
                <ChartBarIcon className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-500">Total Scans</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {stats.totalScans}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0 bg-green-100 rounded-lg p-3">
                <CalendarIcon className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-500">This Month</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {stats.scansThisMonth}/{user?.planLimits?.scansPerMonth || '∞'}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0 bg-yellow-100 rounded-lg p-3">
                <DocumentArrowDownIcon className="h-6 w-6 text-yellow-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-500">Saved Reports</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {stats.savedReportsCount}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0 bg-purple-100 rounded-lg p-3">
                <ChartBarIcon className="h-6 w-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-500">Avg Score</p>
                <p className={`text-2xl font-semibold ${getScoreColor(stats.averageScore)}`}>
                  {stats.averageScore}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px">
              {['overview', 'history', 'saved', 'ai-reports', 'watched', 'settings'].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`py-2 px-6 border-b-2 font-medium text-sm capitalize ${
                    activeTab === tab
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {tab === 'watched' ? 'Watched Domains' : tab}
                </button>
              ))}
            </nav>
          </div>

          <div className="p-6">
            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <>
              
              <GoogleInsightsPanel />

<div>
                <h2 className="text-xl font-semibold mb-4">Recent Activity</h2>
                <div className="space-y-4">
                  {scanHistory.slice(0, 5).map((scan) => (
                    <div key={scan._id} className="flex items-center justify-between py-3 border-b">
                      <div>
                        <p className="font-medium">{scan.url}</p>
                        <p className="text-sm text-gray-500">{formatDate(scan.createdAt)}</p>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className={`font-bold ${getScoreColor(scan.overallScore)}`}>
                          {scan.overallScore}/100
                        </span>
                        <button
                          onClick={() => navigate(`/results/${scan._id}`)}
                          className="text-blue-600 hover:text-blue-700"
                        >
                          <EyeIcon className="h-5 w-5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              </>
            )}

            {/* History Tab */}
            {activeTab === 'history' && (
              <div>
                <h2 className="text-xl font-semibold mb-4">Scan History</h2>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead>
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          URL
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Score
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Pages
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Date
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {scanHistory.map((scan) => (
                        <tr key={scan._id}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <p className="text-sm font-medium text-gray-900">{scan.url}</p>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`font-bold ${getScoreColor(scan.overallScore)}`}>
                              {scan.overallScore}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {scan.pagesScanned}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatDate(scan.createdAt)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <button
                              onClick={() => navigate(`/results/${scan._id}`)}
                              className="text-blue-600 hover:text-blue-900 mr-4"
                            >
                              View
                            </button>
                            <button
                              className="text-green-600 hover:text-green-900"
                            >
                              Save
                            </button>
                          </td>
                        </tr>
                      ))}
              {scanHistory.length === 0 && (
                <tr>
                  <td colSpan="5" className="px-6 py-12 text-center">
                    <p className="text-gray-500 text-lg">No scan history yet</p>
                    <p className="text-gray-400 text-sm mt-2">Start your first AEO scan to see results here.</p>
                  </td>
                </tr>
              )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Saved Reports Tab */}
            {activeTab === 'saved' && (
              <div>
                <h2 className="text-xl font-semibold mb-4">Saved Reports</h2>
                <div className="grid gap-4">
                  {savedReports.map((report) => (
                    <div key={report._id} className="bg-gray-50 rounded-lg p-4 flex justify-between items-center">
                      <div>
                        <p className="font-medium">{report.url}</p>
                        <p className="text-sm text-gray-500">
                          Saved: {formatDate(report.savedAt)} | Score: {report.overallScore}
                        </p>
                        {report.notes && (
                          <p className="text-sm text-gray-600 mt-1">Notes: {report.notes}</p>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => navigate(`/results/${report.scanId}`)}
                          className="text-blue-600 hover:text-blue-700 p-2"
                        >
                          <EyeIcon className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleDeleteReport(report._id)}
                          className="text-red-600 hover:text-red-700 p-2"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      </div>
                    </div>
                  ))}
                  {savedReports.length === 0 && (
                    <p className="text-gray-500 text-center py-8">No saved reports yet</p>
                  )}
                </div>
              </div>
            )}


          {/* AI Reports Tab */}
          {activeTab === 'ai-reports' && (
            <div className="bg-gray-800 rounded-xl p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-white">AI Visibility Reports</h3>
                {aiReportDomains.length > 0 && (
                  <select
                    value={selectedAiDomain}
                    onChange={(e) => setSelectedAiDomain(e.target.value)}
                    className="bg-gray-700 text-white border border-gray-600 rounded-lg px-3 py-2 text-sm"
                  >
                    {aiReportDomains.map((d) => (
                      <option key={d} value={d}>{d}</option>
                    ))}
                  </select>
                )}
              </div>
              {aiReportsLoading ? (
                <p className="text-gray-400 text-center py-8">Loading reports...</p>
              ) : aiReports.length > 0 ? (
                <div className="space-y-3">
                  {aiReports.map((report) => (
                    <div key={report.id} className="bg-gray-700 rounded-lg p-4 flex items-center justify-between">
                      <div>
                        <p className="text-white font-medium capitalize">{report.reportType.replace(/-/g, ' ')}</p>
                        <p className="text-gray-400 text-sm">Last updated: {new Date(report.lastRefreshedAt).toLocaleDateString()}</p>
                      </div>
                      <button
                        onClick={() => navigate('/ai-search-visibility')}
                        className="text-orange-500 hover:text-orange-400 text-sm font-medium"
                      >
                        View Report
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-500">No AI visibility reports yet.</p>
                  <button
                    onClick={() => navigate('/ai-search-visibility')}
                    className="mt-3 text-orange-500 hover:text-orange-400 text-sm font-medium"
                  >
                    Run your first analysis
                  </button>
                </div>
              )}
            </div>
          )}
            {/* Watched Domains Tab */}
            {activeTab === 'watched' && (
              <div>
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-semibold">Watched Domains</h2>
                  <button
                    onClick={handleAddWatchedDomain}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center"
                  >
                    <PlusIcon className="h-5 w-5 mr-2" />
                    Add Domain
                  </button>
                </div>
                <div className="space-y-4">
                  {watchedDomains && watchedDomains.length > 0 ? watchedDomains.map((domain) => (
                    <div key={domain?._id || Math.random()} className="border rounded-lg p-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium">{domain?.domain || domain?.name || 'Unknown Domain'}</p>
                          <p className="text-sm text-gray-500">
                            {domain?.metrics && (
                              <>
                                Health: <span className={getScoreColor(domain.metrics.healthScore)}>
                                  {domain.metrics.healthScore || 'N/A'}
                                </span>
                                {domain.metrics.previousHealthScore && domain.metrics.healthScore !== domain.metrics.previousHealthScore && (
                                  <span className={domain.metrics.healthScore > domain.metrics.previousHealthScore ? 'text-green-600' : 'text-red-600'}>
                                    {' '}({domain.metrics.healthScore > domain.metrics.previousHealthScore ? '+' : ''}
                                    {domain.metrics.healthScore - domain.metrics.previousHealthScore})
                                  </span>
                                )}
                                {' | '}
                                Traffic: {domain.metrics.traffic?.toLocaleString() || 'N/A'}
                                {domain.metrics.previousTraffic && domain.metrics.traffic !== domain.metrics.previousTraffic && (
                                  <span className={domain.metrics.traffic > domain.metrics.previousTraffic ? 'text-green-600' : 'text-red-600'}>
                                    {' '}({domain.metrics.traffic > domain.metrics.previousTraffic ? '+' : ''}
                                    {(domain.metrics.traffic - domain.metrics.previousTraffic).toLocaleString()})
                                  </span>
                                )}
                                {' | '}
                                Backlinks: {domain.metrics.backlinks?.toLocaleString() || 'N/A'}
                                {domain.metrics.previousBacklinks && domain.metrics.backlinks !== domain.metrics.previousBacklinks && (
                                  <span className={domain.metrics.backlinks > domain.metrics.previousBacklinks ? 'text-green-600' : 'text-red-600'}>
                                    {' '}({domain.metrics.backlinks > domain.metrics.previousBacklinks ? '+' : ''}
                                    {(domain.metrics.backlinks - domain.metrics.previousBacklinks).toLocaleString()})
                                  </span>
                                )}
                              </>
                            )}
                          </p>
                          <p className="text-sm text-gray-500">
                            Last checked: {domain?.lastCheck ? formatDate(domain.lastCheck) : 'Never'}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <button 
                            onClick={() => handleCheckDomain(domain)}
                            disabled={checkingDomain === domain._id}
                            className={`font-medium transition-colors ${
                              checkingDomain === domain._id 
                                ? 'text-gray-400 cursor-not-allowed' 
                                : 'text-blue-600 hover:text-blue-700 cursor-pointer'
                            }`}
                          >
                            {checkingDomain === domain._id ? 'Checking...' : 'Check Now'}
                          </button>
                          <button 
                            onClick={() => handleRemoveWatchedDomain(domain._id)}
                            disabled={removingDomain === domain._id}
                            className={`font-medium transition-colors ${
                              removingDomain === domain._id 
                                ? 'text-gray-400 cursor-not-allowed' 
                                : 'text-red-600 hover:text-red-700 cursor-pointer'
                            }`}
                          >
                            {removingDomain === domain._id ? 'Removing...' : 'Remove'}
                          </button>
                        </div>
                      </div>
                    </div>
                  )) : null}
                  {watchedDomains.length === 0 && (
                    <p className="text-gray-500 text-center py-8">
                      No watched domains. Add domains to monitor their AEO scores automatically.
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Settings Tab */}
            {activeTab === 'settings' && (
              <div>
                <h2 className="text-xl font-semibold mb-4">Account Settings</h2>
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-medium mb-2">Current Plan</h3>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="font-semibold capitalize">{user?.plan} Plan</p>
                      <p className="text-sm text-gray-600 mt-1">
                        {user?.planLimits?.scansPerMonth === -1 ? 'Unlimited' : user?.planLimits?.scansPerMonth} scans per month
                      </p>
                      {user?.plan === 'free' && (
                        <button
                          onClick={() => navigate('/pricing')}
                          className="mt-3 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                        >
                          Upgrade Plan
                        </button>
                      )}
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-medium mb-2">Email Notifications</h3>
                    <div className="space-y-2">
                      <label className="flex items-center">
                        <input type="checkbox" className="mr-2" defaultChecked={user?.preferences?.emailNotifications} />
                        <span>Email notifications</span>
                      </label>
                      <label className="flex items-center">
                        <input type="checkbox" className="mr-2" defaultChecked={user?.preferences?.weeklyReports} />
                        <span>Weekly reports</span>
                      </label>
                      <label className="flex items-center">
                        <input type="checkbox" className="mr-2" defaultChecked={user?.preferences?.scanCompleteAlerts} />
                        <span>Scan complete alerts</span>
                      </label>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-medium mb-2">API Access</h3>
                    {user?.plan !== 'free' ? (
                      <div className="bg-gray-50 rounded-lg p-4">
                        <p className="text-sm text-gray-600 mb-2">Your API key for programmatic access:</p>
                        <div className="flex gap-2">
                          <input 
                            type="text" 
                            value={user?.apiKey || 'No API key generated'} 
                            readOnly 
                            className="flex-1 px-3 py-2 border rounded"
                          />
                          <button className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700">
                            Generate New
                          </button>
                        </div>
                      </div>
                    ) : (
                      <p className="text-gray-500">Upgrade to Pro or Plus for API access</p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <FAQSection items={DASHBOARD_FAQ} />
    </DashboardLayout>
    </>
  );
};

export default UserDashboard;